// Fetched via get_starter_files (set: "icons") from hg-standards-mcp — see the icon-system standard.
// Copied verbatim: never edit this file inside a repo. Add or change an icon in
// hg-standards-mcp/content/starter-files/icons/genie-icons.ts, then re-fetch it in every repo.
//
// Every icon is drawn on a 24×24 grid as round-capped 2px strokes or solid shapes, Lucide-compatible
// geometry (ISC licence) where it fits. Genie feature icons carry the Help Genie sparkle; general
// icons set `sparkle: false`. Name icons after what they show ("calendar-check"), never after the
// screen that first used them ("meeting-calendar-card").

export type GenieIconElement = "path" | "circle" | "rect";

/** Attributes of one SVG element. `fill: "paint"` fills the element with the icon's colour or gradient. */
export type GenieIconAttrs = Record<string, string | number>;

export type GenieIconNode = ReadonlyArray<readonly [GenieIconElement, GenieIconAttrs]>;

export interface GenieIconSparkle {
  /** Centre of the sparkle on the 24×24 grid. */
  x: number;
  y: number;
  /** Radius of the four-point star. */
  size: number;
  /** Knock the glyph's strokes out around the sparkle so it stays legible. */
  cutout: boolean;
}

export interface GenieIconDefinition {
  node: GenieIconNode;
  /** Details cut out of the glyph (e.g. text lines on a solid page), drawn with the same attributes. */
  knockout?: GenieIconNode;
  /** Overrides for where the sparkle sits — pick an empty corner of the glyph — or `false` for none. */
  sparkle?: Partial<GenieIconSparkle> | false;
}

export const GENIE_ICON_SPARKLE: GenieIconSparkle = { x: 20, y: 4, size: 3.1, cutout: true };

export const GENIE_ICON_PAINT = "paint";

export const GENIE_ICONS = {
  activity: {
    node: [["path", { d: "M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" }]],
  },
  "ai-chip": {
    node: [
      ["rect", { x: 4, y: 4, width: 16, height: 16, rx: 2 }],
      ["rect", { x: 9, y: 9, width: 6, height: 6, rx: 1 }],
      ["path", { d: "M15 2v2" }],
      ["path", { d: "M15 20v2" }],
      ["path", { d: "M2 15h2" }],
      ["path", { d: "M2 9h2" }],
      ["path", { d: "M20 15h2" }],
      ["path", { d: "M20 9h2" }],
      ["path", { d: "M9 2v2" }],
      ["path", { d: "M9 20v2" }],
    ],
  },
  apps: {
    node: [
      ["rect", { x: 3, y: 3, width: 7, height: 7, rx: 1.5 }],
      ["rect", { x: 3, y: 14, width: 7, height: 7, rx: 1.5 }],
      ["rect", { x: 14, y: 14, width: 7, height: 7, rx: 1.5 }],
    ],
    sparkle: { x: 17.5, y: 6.5, size: 3.6, cutout: false },
  },
  "bar-chart": {
    node: [
      ["path", { d: "M3 3v16a2 2 0 0 0 2 2h16" }],
      ["path", { d: "M18 17V9" }],
      ["path", { d: "M13 17V5" }],
      ["path", { d: "M8 17v-3" }],
    ],
  },
  bell: {
    node: [
      ["path", { d: "M10.268 21a2 2 0 0 0 3.464 0" }],
      ["path", { d: "M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" }],
    ],
  },
  "book-open": {
    node: [
      ["path", { d: "M12 7v14" }],
      ["path", { d: "M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" }],
    ],
  },
  brain: {
    node: [
      ["path", { d: "M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" }],
      ["path", { d: "M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" }],
      ["path", { d: "M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" }],
      ["path", { d: "M17.599 6.5a3 3 0 0 0 .399-1.375" }],
      ["path", { d: "M6.003 5.125A3 3 0 0 0 6.401 6.5" }],
      ["path", { d: "M3.477 10.896a4 4 0 0 1 .585-.396" }],
      ["path", { d: "M19.938 10.5a4 4 0 0 1 .585.396" }],
      ["path", { d: "M6 18a4 4 0 0 1-1.967-.516" }],
      ["path", { d: "M19.967 17.484A4 4 0 0 1 18 18" }],
    ],
  },
  broadcast: {
    node: [
      ["path", { d: "M4.9 19.1C1 15.2 1 8.8 4.9 4.9" }],
      ["path", { d: "M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" }],
      ["circle", { cx: 12, cy: 12, r: 2 }],
      ["path", { d: "M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" }],
      ["path", { d: "M19.1 4.9C23 8.8 23 15.1 19.1 19" }],
    ],
    sparkle: { x: 12, y: 4.2 },
  },
  "calendar-check": {
    node: [
      ["path", { d: "M8 2v4" }],
      ["path", { d: "M16 2v4" }],
      ["rect", { x: 3, y: 4, width: 18, height: 18, rx: 2 }],
      ["path", { d: "M3 10h18" }],
      ["path", { d: "m9 16 2 2 4-4" }],
    ],
  },
  chat: {
    node: [["path", { d: "M7.9 20A9 9 0 1 0 4 16.1L2 22Z" }]],
    sparkle: false,
  },
  "chat-check": {
    node: [
      ["path", { d: "M7.9 20A9 9 0 1 0 4 16.1L2 22Z" }],
      ["path", { d: "m8.5 12 2.5 2.5 4.5-4.5" }],
    ],
    sparkle: false,
  },
  "chat-heart": {
    node: [
      ["path", { d: "M22 17a2 2 0 0 1-2 2H6.83a2 2 0 0 0-1.41.59l-2.2 2.2A.71.71 0 0 1 2 21.29V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" }],
      ["path", { d: "M14.6 8.6c-.7-.7-1.8-.7-2.5 0l-.1.1-.1-.1c-.7-.7-1.8-.7-2.5 0s-.7 1.8 0 2.5L12 13.7l2.6-2.6c.7-.7.7-1.8 0-2.5z" }],
    ],
    sparkle: { x: 20.5, y: 3.5 },
  },
  "chat-play": {
    node: [
      ["path", { d: "M7.9 20A9 9 0 1 0 4 16.1L2 22Z" }],
      ["path", { d: "M10.2 9.4v5.2l4.2-2.6z", fill: GENIE_ICON_PAINT }],
    ],
  },
  "check-circle": {
    node: [["path", { d: "M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z", fill: GENIE_ICON_PAINT }]],
    knockout: [["path", { d: "m8.5 12 2.5 2.5 4.5-4.5", strokeWidth: 2.2 }]],
    sparkle: false,
  },
  clapperboard: {
    node: [
      ["path", { d: "M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3Z" }],
      ["path", { d: "m6.2 5.3 3.1 3.9" }],
      ["path", { d: "m12.4 3.4 3.1 4" }],
      ["path", { d: "M3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" }],
    ],
    sparkle: false,
  },
  "clipboard-check": {
    node: [
      ["rect", { x: 8, y: 2, width: 8, height: 4, rx: 1 }],
      ["path", { d: "M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" }],
      ["path", { d: "m9 14 2 2 4-4" }],
    ],
    sparkle: false,
  },
  code: {
    node: [
      ["path", { d: "m16 18 6-6-6-6" }],
      ["path", { d: "m8 6-6 6 6 6" }],
    ],
    sparkle: false,
  },
  conversation: {
    node: [
      ["path", { d: "M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" }],
      ["path", { d: "M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1" }],
    ],
  },
  dashboard: {
    node: [
      ["rect", { x: 3, y: 3, width: 7, height: 7, rx: 1.5 }],
      ["rect", { x: 3, y: 14, width: 7, height: 7, rx: 1.5 }],
      ["rect", { x: 14, y: 14, width: 7, height: 7, rx: 1.5 }],
    ],
    sparkle: { x: 17.5, y: 6.5, size: 3.6, cutout: false },
  },
  database: {
    node: [
      ["path", { d: "M3 5a9 3 0 1 0 18 0 9 3 0 1 0-18 0" }],
      ["path", { d: "M3 5v14a9 3 0 0 0 18 0V5" }],
      ["path", { d: "M3 12a9 3 0 0 0 18 0" }],
    ],
    sparkle: false,
  },
  document: {
    node: [
      ["path", { d: "M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" }],
      ["path", { d: "M14 2v4a2 2 0 0 0 2 2h4" }],
      ["path", { d: "M10 9H8" }],
      ["path", { d: "M16 13H8" }],
      ["path", { d: "M16 17H8" }],
    ],
  },
  eye: {
    node: [
      ["path", { d: "M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" }],
      ["circle", { cx: 12, cy: 12, r: 3.5, fill: GENIE_ICON_PAINT }],
    ],
    knockout: [["circle", { cx: 13.4, cy: 10.6, r: 0.6, fill: GENIE_ICON_PAINT }]],
    sparkle: false,
  },
  files: {
    node: [
      ["path", { d: "M3.5 7.5v12A2.5 2.5 0 0 0 6 22h9" }],
      ["path", { d: "M9 2h7l4 4v11a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z", fill: GENIE_ICON_PAINT }],
    ],
    knockout: [
      ["path", { d: "M10.5 9h6", strokeWidth: 1.6 }],
      ["path", { d: "M10.5 12.5h6", strokeWidth: 1.6 }],
      ["path", { d: "M10.5 16h3.5", strokeWidth: 1.6 }],
    ],
    sparkle: false,
  },
  flag: {
    node: [["path", { d: "M4 22V4a1 1 0 0 1 .4-.8A6 6 0 0 1 8 2c3 0 5 2 7.333 2q2 0 3.067-.8A1 1 0 0 1 20 4v10a1 1 0 0 1-.4.8A6 6 0 0 1 16 16c-3 0-5-2-8-2a6 6 0 0 0-4 1.528" }]],
    sparkle: { x: 19.5, y: 19.5 },
  },
  flask: {
    node: [
      ["path", { d: "M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2" }],
      ["path", { d: "M6.453 15h11.094" }],
      ["path", { d: "M8.5 2h7" }],
    ],
  },
  gauge: {
    node: [
      ["path", { d: "m12 14 4-4" }],
      ["path", { d: "M3.34 19a10 10 0 1 1 17.32 0" }],
    ],
  },
  "genie-lamp": {
    node: [["path", { d: "M 9.57 5.33 C 9.13 5.79 9.04 6.87 9.44 6.99 C 9.49 7.02 9.63 7.11 9.73 7.2 C 9.98 7.42 9.98 7.42 10.33 7.2 C 10.89 6.84 11.01 6.4 10.71 5.77 C 10.38 5.04 10 4.9 9.57 5.33 M 9.23 7.93 C 8.92 7.99 8.72 8.07 8.5 8.23 C 8.32 8.35 8.15 8.44 8.11 8.44 C 8.07 8.44 7.95 8.53 7.84 8.66 C 7.72 8.78 7.58 8.92 7.53 8.97 C 7.04 9.44 6.94 9.7 7.16 9.89 C 7.3 10.02 12.61 10.09 12.92 9.97 C 13.54 9.71 12.78 8.78 11.38 8.05 C 11.02 7.86 9.96 7.8 9.23 7.93 M 20.92 7.98 C 20.72 8 20.36 8.11 20.15 8.2 C 19.94 8.3 19.68 8.38 19.59 8.4 C 19.48 8.43 19.29 8.52 19.17 8.6 C 19.04 8.7 18.86 8.8 18.74 8.85 C 18.63 8.9 18.46 9 18.35 9.1 C 18.24 9.19 18.07 9.3 17.96 9.35 C 17.84 9.39 17.73 9.48 17.68 9.53 C 17.63 9.58 17.47 9.7 17.3 9.78 C 17.14 9.86 16.91 10 16.81 10.1 C 16.7 10.19 16.58 10.26 16.54 10.26 C 16.49 10.26 16.28 10.36 16.05 10.48 C 15.33 10.86 15.28 10.86 10.73 10.9 C 5.74 10.92 6.32 11.03 5.34 9.96 C 3.97 8.46 1.85 8.6 0.54 10.28 C 0.04 10.92 0.09 12.23 0.67 12.99 C 1.73 14.43 4.25 14.96 5.2 13.95 L 5.32 13.81 5.04 13.51 C 4.72 13.17 4.46 13.08 3.59 12.98 C 2.79 12.89 2.27 12.67 1.94 12.28 L 1.66 11.95 1.68 11.43 L 1.71 10.91 2.04 10.59 C 2.47 10.17 3.08 10.08 3.63 10.36 C 3.8 10.45 4.5 11.29 4.5 11.41 C 4.5 11.45 4.59 11.63 4.71 11.79 C 4.83 11.96 4.94 12.18 4.98 12.29 C 5.01 12.38 5.11 12.54 5.18 12.62 C 5.25 12.7 5.38 12.92 5.46 13.1 C 5.54 13.29 5.66 13.47 5.71 13.48 C 5.77 13.5 5.86 13.65 5.94 13.82 C 6.06 14.1 6.89 15.03 7.02 15.03 C 7.05 15.03 7.18 15.13 7.32 15.24 C 7.46 15.35 7.6 15.44 7.65 15.44 C 7.69 15.44 7.9 15.54 8.11 15.64 C 9.18 16.22 12.14 16.23 13.55 15.67 C 13.78 15.57 14.1 15.48 14.25 15.44 C 14.41 15.41 14.61 15.33 14.69 15.27 C 14.77 15.21 14.97 15.1 15.13 15.04 C 15.29 14.98 15.54 14.84 15.7 14.71 C 15.85 14.6 16.01 14.5 16.04 14.5 C 16.16 14.5 16.95 13.81 17.69 13.03 C 20.93 9.72 21.7 9.13 23.08 8.93 C 23.89 8.82 23.99 8.66 23.51 8.25 C 23.11 7.92 22.08 7.82 20.92 7.98 M 8.66 16.94 C 8.55 17.02 8.44 17.13 8.42 17.17 C 8.38 17.28 7.8 17.66 7.45 17.81 C 7.06 17.97 6.7 18.41 6.76 18.62 C 6.85 18.92 6.9 18.92 10.44 18.92 C 13.94 18.92 14.01 18.92 14.11 18.64 C 14.2 18.43 13.69 17.9 13.28 17.77 C 12.92 17.66 12.34 17.28 12.17 17.08 C 11.96 16.81 11.89 16.8 10.33 16.8 C 8.89 16.8 8.87 16.8 8.66 16.94", fill: GENIE_ICON_PAINT, stroke: "none" }]],
    sparkle: false,
  },
  globe: {
    node: [
      ["circle", { cx: 12, cy: 12, r: 10 }],
      ["path", { d: "M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" }],
      ["path", { d: "M2 12h20" }],
    ],
    sparkle: false,
  },
  lightbulb: {
    node: [
      ["path", { d: "M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" }],
      ["path", { d: "M9 18h6" }],
      ["path", { d: "M10 22h4" }],
    ],
    sparkle: { x: 20.5, y: 3.5 },
  },
  lock: {
    node: [
      ["rect", { x: 3, y: 11, width: 18, height: 11, rx: 2 }],
      ["path", { d: "M8 11V7.5a4 4 0 0 1 8 0V11" }],
      ["path", { d: "M12 15.5v2.5" }],
    ],
    sparkle: { x: 20.5, y: 3.5 },
  },
  mail: {
    node: [
      ["rect", { x: 2, y: 4, width: 20, height: 16, rx: 2 }],
      ["path", { d: "m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" }],
    ],
  },
  microphone: {
    node: [
      ["path", { d: "M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" }],
      ["path", { d: "M19 10v2a7 7 0 0 1-14 0v-2" }],
      ["path", { d: "M12 19v3" }],
    ],
  },
  palette: {
    node: [
      ["circle", { cx: 13.5, cy: 6.5, r: 0.6, fill: GENIE_ICON_PAINT }],
      ["circle", { cx: 17.5, cy: 10.5, r: 0.6, fill: GENIE_ICON_PAINT }],
      ["circle", { cx: 8.5, cy: 7.5, r: 0.6, fill: GENIE_ICON_PAINT }],
      ["circle", { cx: 6.5, cy: 12.5, r: 0.6, fill: GENIE_ICON_PAINT }],
      ["path", { d: "M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" }],
    ],
  },
  paperclip: {
    node: [["path", { d: "m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" }]],
    sparkle: { x: 3.8, y: 3.8 },
  },
  phone: {
    node: [["path", { d: "M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" }]],
    sparkle: false,
  },
  "phone-outgoing": {
    node: [
      ["path", { d: "m16 8 6-6" }],
      ["path", { d: "M22 8V2h-6" }],
      ["path", { d: "M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" }],
    ],
    sparkle: { x: 13, y: 11, size: 2.6, cutout: false },
  },
  play: {
    node: [["path", { d: "M7 4.7v14.6a1.2 1.2 0 0 0 1.8 1l11.6-7.3a1.2 1.2 0 0 0 0-2L8.8 3.7A1.2 1.2 0 0 0 7 4.7z", fill: GENIE_ICON_PAINT }]],
    sparkle: false,
  },
  "qr-code": {
    node: [
      ["rect", { x: 3, y: 3, width: 5, height: 5, rx: 1 }],
      ["rect", { x: 16, y: 3, width: 5, height: 5, rx: 1 }],
      ["rect", { x: 3, y: 16, width: 5, height: 5, rx: 1 }],
      ["path", { d: "M21 16h-3a2 2 0 0 0-2 2v3" }],
      ["path", { d: "M21 21v.01" }],
      ["path", { d: "M12 7v3a2 2 0 0 1-2 2H7" }],
      ["path", { d: "M3 12h.01" }],
      ["path", { d: "M12 3h.01" }],
      ["path", { d: "M12 16v.01" }],
      ["path", { d: "M16 12h1" }],
      ["path", { d: "M21 12v.01" }],
      ["path", { d: "M12 21v-1" }],
    ],
    sparkle: false,
  },
  scroll: {
    node: [
      ["path", { d: "M15 12h-5" }],
      ["path", { d: "M15 8h-5" }],
      ["path", { d: "M19 17V5a2 2 0 0 0-2-2H4" }],
      ["path", { d: "M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3" }],
    ],
  },
  settings: {
    node: [
      ["path", { d: "M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" }],
      ["circle", { cx: 12, cy: 12, r: 3 }],
    ],
    sparkle: false,
  },
  share: {
    node: [
      ["circle", { cx: 18, cy: 5, r: 3 }],
      ["circle", { cx: 6, cy: 12, r: 3 }],
      ["circle", { cx: 18, cy: 19, r: 3 }],
      ["path", { d: "m8.59 13.51 6.83 3.98" }],
      ["path", { d: "m15.41 6.51-6.82 3.98" }],
    ],
    sparkle: { x: 4.2, y: 4.2 },
  },
  "shield-check": {
    node: [
      ["path", { d: "M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" }],
      ["path", { d: "m9 12 2 2 4-4" }],
    ],
  },
  sliders: {
    node: [
      ["path", { d: "M5 3v5" }],
      ["circle", { cx: 5, cy: 10, r: 2 }],
      ["path", { d: "M5 12v9" }],
      ["path", { d: "M12 3v11" }],
      ["circle", { cx: 12, cy: 16, r: 2 }],
      ["path", { d: "M12 18v3" }],
      ["circle", { cx: 19, cy: 11, r: 2 }],
      ["path", { d: "M19 13v8" }],
    ],
  },
  smile: {
    node: [["circle", { cx: 12, cy: 12, r: 10, fill: GENIE_ICON_PAINT }]],
    knockout: [
      ["circle", { cx: 8.5, cy: 9.5, r: 0.9, fill: GENIE_ICON_PAINT }],
      ["circle", { cx: 15.5, cy: 9.5, r: 0.9, fill: GENIE_ICON_PAINT }],
      ["path", { d: "M7.5 13.5s1.5 3.5 4.5 3.5 4.5-3.5 4.5-3.5", strokeWidth: 2.2 }],
    ],
    sparkle: false,
  },
  "sound-wave": {
    node: [
      ["path", { d: "M2 10v4" }],
      ["path", { d: "M6 9v6" }],
      ["path", { d: "M10 4v16" }],
      ["path", { d: "M14 7v10" }],
      ["path", { d: "M18 5v14" }],
      ["path", { d: "M22 10v4" }],
    ],
    sparkle: { x: 4, y: 4 },
  },
  sparkles: {
    node: [
      ["path", { d: "M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" }],
      ["path", { d: "M20 3v4" }],
      ["path", { d: "M22 5h-4" }],
      ["path", { d: "M4 17v2" }],
      ["path", { d: "M5 18H3" }],
    ],
    sparkle: false,
  },
  sync: {
    node: [
      ["path", { d: "M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" }],
      ["path", { d: "M21 3v5h-5" }],
      ["path", { d: "M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" }],
      ["path", { d: "M8 16H3v5" }],
    ],
    sparkle: { x: 12, y: 12, size: 3.4, cutout: false },
  },
  target: {
    node: [
      ["circle", { cx: 12, cy: 12, r: 1, fill: GENIE_ICON_PAINT }],
      ["path", { d: "M12 7a5 5 0 1 0 5 5" }],
      ["path", { d: "M13 3.055a9 9 0 1 0 7.941 7.945" }],
      ["path", { d: "M15 6v3h3l3-3h-3V3z", fill: GENIE_ICON_PAINT }],
      ["path", { d: "m15 9-3 3" }],
    ],
    sparkle: false,
  },
  "trending-up": {
    node: [
      ["path", { d: "M16 7h6v6" }],
      ["path", { d: "m22 7-8.5 8.5-5-5L2 17" }],
    ],
    sparkle: false,
  },
  user: {
    node: [
      ["path", { d: "M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" }],
      ["circle", { cx: 12, cy: 7, r: 4 }],
    ],
    sparkle: false,
  },
  "user-check": {
    node: [
      ["path", { d: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" }],
      ["circle", { cx: 9, cy: 7, r: 4 }],
      ["path", { d: "m16 11 2 2 4-4" }],
    ],
    sparkle: false,
  },
  users: {
    node: [
      ["path", { d: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" }],
      ["circle", { cx: 9, cy: 7, r: 4 }],
      ["path", { d: "M22 21v-2a4 4 0 0 0-3-3.87" }],
      ["path", { d: "M16 3.13a4 4 0 0 1 0 7.75" }],
    ],
    sparkle: false,
  },
  video: {
    node: [
      ["path", { d: "m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" }],
      ["rect", { x: 2, y: 6, width: 14, height: 12, rx: 2 }],
    ],
  },
  "web-page": {
    node: [
      ["rect", { x: 2, y: 4, width: 20, height: 16, rx: 2 }],
      ["path", { d: "M2 8.5h20" }],
      ["circle", { cx: 12, cy: 14.25, r: 3.25 }],
      ["path", { d: "M8.75 14.25h6.5" }],
      ["path", { d: "M12 11a5 5 0 0 1 0 6.5 5 5 0 0 1 0-6.5" }],
    ],
    sparkle: { x: 20.5, y: 3.5 },
  },
  webhook: {
    node: [
      ["path", { d: "M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2" }],
      ["path", { d: "m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06" }],
      ["path", { d: "m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8" }],
    ],
  },
  wifi: {
    node: [["path", { d: "M 11.03 4.8 C 10.16 4.85 9.91 4.88 9.53 5.03 C 9.36 5.09 9.03 5.18 8.8 5.22 C 8.57 5.26 8.18 5.38 7.95 5.47 C 7.72 5.57 7.49 5.65 7.44 5.65 C 7.4 5.65 7.28 5.71 7.17 5.78 C 7.07 5.85 6.84 5.96 6.66 6.02 C 6.49 6.09 6.29 6.19 6.23 6.24 C 6.17 6.3 6.02 6.39 5.89 6.44 C 5.77 6.5 5.65 6.57 5.63 6.6 C 5.61 6.63 5.48 6.71 5.33 6.78 C 5.19 6.85 5 6.98 4.9 7.06 C 4.8 7.14 4.6 7.3 4.45 7.43 C 3.67 8.06 3.24 8.49 3.08 8.81 L 2.94 9.09 3.06 9.36 C 3.29 9.85 3.61 10.06 4.01 9.98 C 4.17 9.95 4.33 9.82 5.21 9.05 C 5.33 8.94 5.46 8.85 5.48 8.85 C 5.51 8.85 5.64 8.76 5.77 8.65 C 5.91 8.53 6.11 8.4 6.21 8.35 C 6.31 8.29 6.41 8.23 6.43 8.2 C 6.44 8.17 6.6 8.08 6.77 8 C 6.94 7.91 7.15 7.8 7.25 7.75 C 7.64 7.54 8.29 7.26 8.45 7.23 C 8.55 7.21 8.76 7.13 8.93 7.05 C 9.15 6.94 9.43 6.87 10.05 6.78 C 10.5 6.71 11.02 6.62 11.2 6.59 C 11.61 6.51 12.96 6.51 13.23 6.59 C 13.34 6.62 13.79 6.7 14.24 6.77 C 14.81 6.86 15.12 6.94 15.29 7.02 C 15.42 7.09 15.68 7.18 15.85 7.22 C 16.03 7.27 16.25 7.36 16.35 7.42 C 16.45 7.49 16.64 7.58 16.78 7.62 C 16.93 7.67 17.07 7.74 17.11 7.78 C 17.14 7.83 17.31 7.93 17.47 8.01 C 17.63 8.09 17.82 8.19 17.87 8.24 C 17.93 8.3 18.07 8.39 18.18 8.44 C 18.3 8.5 18.41 8.59 18.44 8.63 C 18.46 8.67 18.56 8.76 18.66 8.82 C 18.76 8.88 19 9.06 19.2 9.22 C 20.04 9.93 20.19 10.02 20.46 9.99 C 20.67 9.96 21.17 9.56 21.22 9.38 C 21.34 8.93 21.11 8.5 20.47 8 C 20.38 7.93 20.14 7.74 19.93 7.57 C 19.73 7.4 19.49 7.22 19.41 7.18 C 19.33 7.14 19.25 7.07 19.23 7.02 C 19.21 6.98 19.1 6.9 18.98 6.85 C 18.85 6.79 18.69 6.69 18.61 6.62 C 18.53 6.55 18.37 6.46 18.27 6.42 C 18.16 6.37 18.01 6.29 17.94 6.22 C 17.86 6.16 17.68 6.07 17.54 6.03 C 17.39 5.98 17.2 5.89 17.09 5.83 C 16.99 5.76 16.78 5.67 16.62 5.62 C 16.46 5.58 16.24 5.49 16.13 5.44 C 16.02 5.38 15.69 5.29 15.38 5.22 C 15.07 5.16 14.71 5.06 14.58 5.01 C 14.1 4.83 12.38 4.73 11.03 4.8 M 11.25 8.68 C 11.02 8.71 10.57 8.78 10.25 8.82 C 9.87 8.87 9.59 8.94 9.43 9.02 C 9.29 9.09 9.04 9.19 8.87 9.24 C 8.7 9.29 8.5 9.38 8.42 9.43 C 8.34 9.49 8.16 9.57 8.03 9.62 C 7.89 9.67 7.72 9.77 7.65 9.83 C 7.58 9.89 7.46 9.97 7.39 10 C 7.31 10.02 7.07 10.19 6.85 10.36 C 6.63 10.53 6.37 10.73 6.28 10.8 C 5.5 11.4 5.36 12.05 5.93 12.52 C 6.26 12.79 6.56 12.84 6.75 12.65 C 6.81 12.59 6.92 12.51 6.99 12.46 C 7.13 12.38 7.21 12.31 7.72 11.89 C 7.86 11.78 8.05 11.65 8.15 11.61 C 8.25 11.57 8.37 11.5 8.43 11.45 C 8.48 11.4 8.66 11.29 8.83 11.22 C 8.99 11.14 9.18 11.04 9.25 10.99 C 9.32 10.94 9.57 10.86 9.81 10.8 C 10.04 10.75 10.35 10.66 10.49 10.6 C 11.21 10.32 12.95 10.32 13.8 10.61 C 14.09 10.71 14.4 10.8 14.5 10.82 C 14.71 10.87 15.66 11.32 15.74 11.42 C 15.77 11.45 15.9 11.53 16.01 11.58 C 16.13 11.63 16.32 11.75 16.44 11.85 C 16.55 11.95 16.75 12.11 16.89 12.22 C 17.02 12.32 17.22 12.48 17.34 12.58 C 17.63 12.8 17.9 12.81 18.18 12.61 C 18.79 12.17 18.77 11.6 18.13 11.03 C 17.96 10.87 17.76 10.68 17.68 10.6 C 17.59 10.52 17.46 10.43 17.37 10.38 C 17.29 10.34 17.19 10.27 17.15 10.22 C 17.11 10.18 16.96 10.08 16.8 10 C 16.65 9.92 16.48 9.81 16.43 9.76 C 16.38 9.7 16.31 9.65 16.27 9.65 C 16.24 9.65 16.06 9.57 15.87 9.47 C 15.69 9.38 15.43 9.27 15.3 9.23 C 15.17 9.19 14.94 9.1 14.78 9.03 C 14.21 8.76 12.09 8.55 11.25 8.68 M 11.3 12.5 C 11.17 12.52 11 12.57 10.92 12.62 C 10.84 12.66 10.59 12.74 10.38 12.79 C 10.17 12.85 9.91 12.95 9.8 13.02 C 9.7 13.09 9.53 13.18 9.44 13.22 C 8.39 13.65 7.99 14.85 8.77 15.2 C 9.14 15.37 9.74 15.28 9.95 15.02 C 9.99 14.97 10.14 14.88 10.28 14.82 C 10.41 14.75 10.6 14.64 10.69 14.58 C 11.14 14.25 12.98 14.23 13.47 14.55 C 13.56 14.6 13.75 14.71 13.91 14.79 C 14.07 14.88 14.27 15 14.35 15.07 C 14.92 15.55 15.85 15.2 15.85 14.51 C 15.85 14.32 15.61 13.86 15.44 13.73 C 15.36 13.67 15.21 13.55 15.11 13.47 C 15.01 13.38 14.82 13.26 14.7 13.2 C 14.58 13.14 14.42 13.05 14.35 13 C 14.28 12.95 14.03 12.86 13.8 12.79 C 13.57 12.72 13.27 12.62 13.15 12.57 C 12.89 12.48 11.69 12.43 11.3 12.5 M 11.85 16.1 C 10.8 16.31 10.03 17.79 10.58 18.52 C 10.62 18.58 10.72 18.73 10.81 18.87 C 10.92 19.03 11.05 19.15 11.22 19.24 C 11.36 19.3 11.53 19.41 11.6 19.46 C 11.68 19.52 11.85 19.57 12.03 19.59 C 12.34 19.61 12.4 19.6 12.98 19.29 C 14.01 18.74 14.01 16.89 12.98 16.37 C 12.87 16.31 12.71 16.23 12.63 16.19 C 12.44 16.09 12.08 16.05 11.85 16.1", fill: GENIE_ICON_PAINT, stroke: "none" }]],
    sparkle: false,
  },
  "wizard-hat": {
    node: [["path", { d: "M 12.8 3.59 C 12.6 3.65 12.39 3.75 12.23 3.86 C 12.1 3.97 11.97 4.05 11.95 4.05 C 11.92 4.05 11.82 4.13 11.71 4.22 C 11.61 4.31 11.48 4.41 11.43 4.43 C 11.14 4.59 10 5.76 10 5.9 C 10 5.92 9.92 6.03 9.83 6.14 C 9.74 6.25 9.64 6.42 9.6 6.51 C 9.56 6.6 9.48 6.74 9.42 6.81 C 9.36 6.89 9.26 7.09 9.2 7.27 C 9.14 7.45 9.06 7.62 9.02 7.65 C 8.98 7.69 8.9 7.86 8.85 8.04 C 8.79 8.22 8.7 8.44 8.65 8.52 C 8.6 8.61 8.51 8.87 8.45 9.11 C 8.4 9.34 8.3 9.64 8.23 9.77 C 8.16 9.89 8.08 10.15 8.05 10.34 C 7.94 10.98 7.77 11.14 7.06 11.24 C 6.76 11.28 6.51 11.34 6.4 11.41 C 6.31 11.47 6.11 11.55 5.96 11.6 C 5.56 11.74 4.96 12.25 4.76 12.62 C 4.69 12.75 4.6 12.91 4.56 12.97 C 4.38 13.27 4.6 13.99 4.97 14.35 C 5.02 14.39 5.13 14.5 5.21 14.58 C 5.3 14.67 5.45 14.78 5.55 14.83 C 5.65 14.87 5.79 14.96 5.88 15.03 C 5.96 15.09 6.17 15.18 6.33 15.23 C 6.49 15.28 6.7 15.37 6.8 15.43 C 6.91 15.5 7.18 15.58 7.53 15.65 C 7.83 15.7 8.21 15.79 8.36 15.85 C 9.3 16.18 14.36 16.18 15.29 15.85 C 15.45 15.79 15.82 15.7 16.11 15.65 C 16.45 15.59 16.73 15.51 16.86 15.43 C 16.98 15.36 17.21 15.27 17.37 15.23 C 17.53 15.19 17.76 15.09 17.87 15.01 C 17.98 14.93 18.13 14.85 18.19 14.83 C 18.29 14.8 18.45 14.66 18.76 14.37 C 19.38 13.77 19.46 12.99 18.95 12.43 C 18.55 11.98 18.27 11.77 17.94 11.64 C 17.79 11.59 17.57 11.49 17.45 11.43 C 17.32 11.36 17.06 11.28 16.81 11.25 C 16.12 11.16 16.09 11.13 15.97 10.63 C 15.93 10.44 15.84 10.19 15.78 10.08 C 15.71 9.97 15.64 9.8 15.63 9.7 C 15.61 9.6 15.52 9.41 15.44 9.26 C 15.36 9.12 15.25 8.88 15.2 8.74 C 15.14 8.6 15.05 8.43 15 8.36 C 14.95 8.3 14.87 8.13 14.82 7.99 C 14.78 7.85 14.68 7.62 14.6 7.47 C 14.28 6.86 14.44 6.11 14.78 6.63 C 14.83 6.7 14.98 6.82 15.11 6.89 C 15.25 6.96 15.44 7.07 15.54 7.12 C 15.79 7.26 16.83 7.27 17.1 7.14 C 18.05 6.68 18.43 6.16 18.49 5.28 C 18.56 3.96 17.63 3.62 17.35 4.87 C 17.24 5.35 16.75 5.39 16.16 4.96 C 16.03 4.87 15.91 4.8 15.87 4.8 C 15.83 4.8 15.73 4.74 15.64 4.66 C 15.55 4.58 15.38 4.47 15.27 4.41 C 15.16 4.35 15.04 4.27 15 4.22 C 14.96 4.18 14.82 4.09 14.69 4.02 C 14.56 3.96 14.38 3.85 14.28 3.78 C 13.92 3.53 13.31 3.46 12.8 3.59 M 14.62 16.65 C 14.23 16.79 13.57 16.85 12.5 16.85 C 11.26 16.86 11 16.95 11.37 17.24 C 11.81 17.58 11.65 18.23 11.07 18.45 C 10.92 18.5 10.75 18.58 10.69 18.62 C 10.62 18.66 10.45 18.74 10.3 18.8 C 9.54 19.08 9.04 19.93 9.26 20.58 C 9.41 21.02 10.2 21.04 10.68 20.6 C 10.79 20.5 10.96 20.4 11.06 20.38 C 11.15 20.36 11.39 20.29 11.6 20.23 C 11.81 20.16 12.22 20.09 12.53 20.05 C 13.16 19.98 13.35 19.93 13.58 19.77 C 13.68 19.71 13.82 19.64 13.91 19.62 C 14.1 19.58 14.71 19.01 14.83 18.76 C 14.88 18.66 14.97 18.51 15.03 18.43 C 15.21 18.18 15.26 17.91 15.24 17.34 C 15.21 16.56 15.13 16.47 14.62 16.65", fill: GENIE_ICON_PAINT, stroke: "none" }]],
    sparkle: false,
  },
  wrench: {
    node: [["path", { d: "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" }]],
    sparkle: { x: 4.2, y: 4.2 },
  },
} satisfies Record<string, GenieIconDefinition>;

export type GenieIconName = keyof typeof GENIE_ICONS;
